import React, { useMemo } from 'react';
import { Autocomplete, TextField, type TextFieldProps } from '@mui/material';
import type { DynamicEntityRow } from '../api/strapiDynamicEntity';
import { useAuth } from '../auth/AuthContext';
import { useMasterEntityMiniListQuery } from '../query/hooks';

export function jenisPengukuranMasterOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const nama = String(row.nama_jenis ?? '');
  const kode = String(row.kode_jenis ?? '');
  return [nama, kode].filter(Boolean).join(' — ') || String(row.documentId ?? '');
}

type Props = {
  value: DynamicEntityRow | null;
  onChange: (row: DynamicEntityRow | null) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  /** Untuk Autocomplete `input` (Cypress / a11y). */
  inputTestId?: string;
  textFieldProps?: Partial<TextFieldProps>;
};

/** Autocomplete ke master `jenis-pengukurans` (Strapi documentId). */
export function JenisPengukuranMasterAutocomplete({
  value,
  onChange,
  disabled = false,
  label = 'Jenis pengukuran',
  required = false,
  inputTestId,
  textFieldProps,
}: Props) {
  const { jwt } = useAuth();
  const listQ = useMasterEntityMiniListQuery(jwt, 'jenis-pengukurans', Boolean(jwt));

  const options = useMemo(() => {
    const rows = listQ.data ?? [];
    return [...rows].sort((a, b) =>
      jenisPengukuranMasterOptionLabel(a).localeCompare(jenisPengukuranMasterOptionLabel(b), 'id'),
    );
  }, [listQ.data]);

  return (
    <Autocomplete<DynamicEntityRow, false, false, false>
      options={options}
      loading={listQ.isPending}
      disabled={disabled}
      value={value}
      onChange={(_, v) => onChange(v)}
      getOptionLabel={(o) => jenisPengukuranMasterOptionLabel(o)}
      isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          label={label}
          required={required}
          size="small"
          fullWidth
          error={Boolean(textFieldProps?.error)}
          helperText={textFieldProps?.helperText}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              ...(inputTestId ? { 'data-testid': inputTestId } : {}),
            },
          }}
        />
      )}
    />
  );
}
